import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/src/components/ui/popover';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { useStudent } from '../hooks/useStudents';
import { ageOf } from '../lib/studentName';

/**
 * A glance beside a student's name, not a context switch — this is a Popover,
 * not a Dialog. Tapping a name used to navigate straight to the student page,
 * which mid-attendance threw away the screen the teacher was on just to check
 * one fact. Here the full name is the only way out, one tap further in.
 *
 * The participant DTO this wraps only carries `studentId` + `studentName`, so
 * the fetch is lazy: nothing hits the network until the popover opens
 * (`useStudent` is `enabled: id !== null`), and TanStack Query caches the
 * result for the next open.
 */
export function StudentNamePopover({
  studentId, children,
}: {
  studentId: string;
  children: React.ReactNode;
}): React.ReactElement {
  const { t } = useTranslation('students');
  const [open, setOpen] = useState(false);
  const { data: student, isLoading, isError } = useStudent(open ? studentId : null);
  const age = student?.dateOfBirth ? ageOf(student.dateOfBirth) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="underline decoration-dashed decoration-surface-300 underline-offset-4 hover:decoration-surface-500"
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64" collisionPadding={12}>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        )}
        {isError && <p className="text-sm text-muted-foreground">{t('popover.loadError')}</p>}
        {student && (
          <div className="space-y-1.5 text-sm">
            <p className="font-medium">
              <Link
                to={`/students/${studentId}`}
                aria-label={t('popover.openProfile')}
                className="text-brand-700 hover:underline"
              >
                {student.name}
              </Link>
              {student.nickname && (
                <span className="ml-1 font-normal text-muted-foreground">({student.nickname})</span>
              )}
            </p>
            {/* `clientId` is NOT NULL, so a payer should always be here — but
                StudentDetailPage renders this same field defensively anyway, and
                a popover that throws on open is a worse failure than a missing
                line. Follow the precedent rather than trusting the type.

                Every row below is genuinely optional and is omitted entirely
                rather than shown as "Giới tính: —" when absent. */}
            {student.client && (
              <p>
                <span className="text-muted-foreground">{t('popover.payer')}: </span>
                {student.client.name}
                {student.client.zaloPhone && ` · ${student.client.zaloPhone}`}
              </p>
            )}
            {student.gender && (
              <p>
                <span className="text-muted-foreground">{t('popover.gender')}: </span>
                {student.gender === 'MALE' ? t('fields.genderMale') : t('fields.genderFemale')}
              </p>
            )}
            {student.dateOfBirth && (
              <p>
                <span className="text-muted-foreground">{t('popover.dob')}: </span>
                {format(new Date(student.dateOfBirth), 'dd/MM/yyyy')}
                {age !== null && ` (${t('detail.age', { count: age })})`}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
